"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Quote, Sword, Cross, Shield, Scroll } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FraKaosTilKarakter() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const chapters = [{
    title: "Disciplinens Fundament",
    content: "I Den Kongelige Livgarde lærte jeg, at disciplin ikke er en straf, men en befrielse. Når uniformen sidder perfekt, og eksercitsen udføres med maskinel præcision, forsvinder individets tvivl til fordel for korpsets styrke. Men da jeg trådte ud af geledderne og ind i det civile liv, smuldrede strukturen. Uden en befalingsmand til at udstikke retningen, blev friheden til et fængsel af ligegyldighed. Det var her, i tomrummet mellem pligt og lyst, at kaosset slog rod.",
    note: "Disciplin uden et højere formål er blot dressur. Sand karakter kræver en indre kompasnål."
  }, {
    title: "Det Moralske Forfald",
    content: "Vi lever i en tid, hvor følelser trumfer fakta, og hvor bekvemmelighed er blevet vores nye gud. Jeg så det i spejlet, og jeg ser det i samfundet. Vi har accepteret det uacceptable. Over 1.000.000 aborter i Danmark er ikke blot statistik; det er et monument over vores kollektive svigt. Vi har byttet livets hellighed ud med retten til at vælge fra, når ansvaret bliver for tungt. Det er en kultur, der hylder offerrollen frem for pligten, og som frygter sandheden mere end løgnen.",
    note: "Et samfund måles ikke på dets rigdom, men på hvordan det beskytter sine mindste og mest forsvarsløse."
  }, {
    title: "Genopbygningen",
    content: "Vejen tilbage fra kaos går gennem erkendelse og bøn. Da jeg fandt tilbage til troen, fandt jeg også den ultimative befalingsmand. Kristendommen er ikke en blød pude, men et sværd, der skiller ret fra uret. At stå fast på de ufødtes rettigheder og tale tidsånden imod kræver mere end blot holdninger; det kræver karakter. Dette er min mission nu: At forene garderns disciplin med den kristne etik. At stå vagt om de værdier, der har bygget vores civilisation, og som vi er ved at sætte over styr.",
    note: "Tro er ikke en følelse. Det er en handling. Det er at gøre det rette, selv når det koster."
  }];
  return <section id="fra-kaos-til-karakter" ref={sectionRef} className="dark bg-background text-foreground py-24 relative overflow-hidden bg-[hsl(40,20%,94%)] text-[hsl(220,15%,5%)]">
      {}
      <div className="absolute top-0 left-0 w-full h-4 bg-[hsl(220,15%,5%)] z-10" />
      <div className="absolute bottom-0 left-0 w-full h-4 bg-[hsl(220,15%,5%)] z-10" />
      
      {}
      <div className="absolute top-0 bottom-0 left-6 md:left-12 w-px bg-[hsl(220,15%,5%)] opacity-20 hidden md:block" />
      <div className="absolute top-0 bottom-0 right-6 md:right-12 w-px bg-[hsl(220,15%,5%)] opacity-20 hidden md:block" />

      <div className={`container mx-auto px-4 md:px-8 transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
        
        {}
        <div className="max-w-[700px] mx-auto mb-16 text-center">
          <div className="inline-flex items-center justify-center gap-3 mb-6">
            <Sword className="w-6 h-6 text-[hsl(220,15%,5%)]" />
            <span className="uppercase tracking-widest text-sm font-bold border-b-2 border-[hsl(220,15%,5%)] pb-1">
              Manifest
            </span>
            <Sword className="w-6 h-6 text-[hsl(220,15%,5%)] scale-x-[-1]" />
          </div>
          <h2 className="text-4xl md:text-6xl font-serif font-black tracking-tight mb-6 leading-[0.9]">
            FRA KAOS TIL KARAKTER
          </h2>
          <p className="text-lg md:text-xl font-serif italic text-[hsl(220,10%,35%)]">
            En Garders Bekendelser om Tro, Pligt og Det Moderne Forfald
          </p>
        </div>

        {}
        <div className="relative max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_700px_1fr] gap-8">
            
            {}
            <div className="hidden xl:flex flex-col items-end pt-4">
              <div className="sticky top-32 flex flex-col gap-8 items-end text-right">
                <div className="w-12 h-1 bg-[hsl(220,15%,5%)]" />
                <span className="font-mono text-xs uppercase tracking-widest text-[hsl(220,10%,40%)] rotate-90 origin-right translate-x-2 translate-y-8">
                  Est. 2024
                </span>
              </div>
            </div>

            {}
            <div className="max-w-[700px] mx-auto xl:mx-0">
              {chapters.map((chapter, i) => <div key={i} data-index={i} className="mb-16 relative group">
                  <h3 className="text-2xl font-bold font-serif mb-4 uppercase tracking-tight border-b-2 border-[hsl(220,15%,5%)] inline-block pb-1">
                    {chapter.title}
                  </h3>
                  
                  <div className="relative">
                    <p className="text-lg md:text-xl leading-relaxed font-serif text-justify">
                      <span className="float-left text-6xl md:text-7xl font-black font-serif mr-4 mt-[-8px] leading-[0.8] text-[hsl(220,15%,5%)]">
                        {chapter.content.charAt(0)}
                      </span>
                      {chapter.content.slice(1)}
                    </p>

                    {}
                    <div className="xl:hidden mt-6 pl-6 border-l-4 border-primary bg-[hsl(40,20%,90%)] p-4">
                      <Quote className="w-5 h-5 text-primary mb-2" />
                      <p className="text-primary font-bold font-serif italic text-lg">
                        {chapter.note}
                      </p>
                    </div>
                  </div>

                  {}
                  <aside className="hidden xl:block absolute top-12 right-[-340px] w-[280px]">
                    <div className="relative p-6 border-l-2 border-primary/30">
                      <Quote className="absolute -top-3 -left-3 w-6 h-6 text-primary bg-[hsl(40,20%,94%)]" />
                      <p className="text-primary font-bold font-serif italic text-lg leading-tight">
                        "{chapter.note}"
                      </p>
                      <div className="mt-4 w-8 h-px bg-primary" />
                    </div>
                  </aside>
                </div>)}

              {}
              <div className="flex flex-col items-center justify-center mt-20 mb-12 gap-4">
                <div className="w-full h-px bg-[hsl(220,15%,5%)] opacity-20" />
                <Cross className="w-8 h-8 text-[hsl(220,15%,5%)]" />
                <p className="font-mono text-sm uppercase tracking-widest">Christian Bak</p>
                <div className="w-full h-px bg-[hsl(220,15%,5%)] opacity-20" />
              </div>

              {}
              <div className="flex justify-center">
                <Button asChild size="lg" className="bg-[hsl(220,15%,5%)] text-[hsl(40,20%,94%)] hover:bg-[hsl(220,15%,5%)]/90 rounded-none border-2 border-transparent hover:border-[hsl(45,50%,50%)] transition-all">
                  <Link to="/JourneyPage">
                    <Scroll className="mr-2 w-4 h-4" />
                    Læs hele historien
                  </Link>
                </Button>
              </div>
            </div>

            {}
            <div className="hidden xl:block">
              {}
            </div>

          </div>
        </div>
      </div>
    </section>;
}